// Which UPI VPA(s) a payment actually settles into. The backend is the source of
// truth: GET /merchants/me returns a `routing` plan computed by vpa_selector.
// `routingPlan` below prefers it and only falls back to a local mirror of the
// same rules (for an older backend mid-deploy). Rules, per kind — P2M routes to
// "merchant" VPAs, P2P to "personal" ones, never mixed:
//   - only ACTIVE, real VPAs count (a sign-up placeholder like x@superbank never
//     routes); a VPA that hit its daily cap is skipped unless every one is capped
//     (then all stay routable — never reject a payer);
//   - auto-shuffle ON:  spread evenly across every active VPA (least-used next);
//   - auto-shuffle OFF: the default VPA first, then priority; the rest are standby.
// When a mode has no active VPA, payments fall back to the other mode's pool
// (merchant setting mode_fallback, default on) — see `routes_to`.

const PRIO_NONE = 10000;
const prio = (u) => (Number.isInteger(u.priority) ? u.priority : PRIO_NONE);
const kindOf = (u) => u.kind || "merchant";
export const isPlaceholder = (vpa) => /@superbank$/i.test((vpa || "").trim());
const isActive = (u) => u && u.active !== false && !!u.vpa && !isPlaceholder(u.vpa);
const capOf = (u) => (u.max_txns && u.max_txns > 0 ? u.max_txns : null);
const isCapped = (u) => capOf(u) !== null && (u.today_count || 0) >= capOf(u);

function ordered(active, autoshuffle) {
  const pool = active.filter((u) => !isCapped(u));
  const list = [...(pool.length ? pool : active)];
  const created = (a, b) => (a.created_at || "").localeCompare(b.created_at || "");
  list.sort((a, b) => {
    if (!autoshuffle && !!a.is_default !== !!b.is_default) return a.is_default ? -1 : 1;
    if (prio(a) !== prio(b)) return prio(a) - prio(b);
    if ((a.today_count || 0) !== (b.today_count || 0)) return (a.today_count || 0) - (b.today_count || 0);
    return created(a, b);
  });
  return list;
}

const row = (u) => ({ id: u.id, vpa: u.vpa, label: u.label || "", today_count: u.today_count || 0, max_txns: capOf(u) });

function planForKind(upiIds, kind, autoshuffle) {
  const all = (upiIds || []).filter((u) => u && kindOf(u) === kind);
  const ofKind = all.filter((u) => !isPlaceholder(u.vpa));
  const active = ofKind.filter(isActive);
  const routable = ordered(active, autoshuffle);
  const live = autoshuffle ? routable : routable.slice(0, 1);
  const capped = active.filter(isCapped);
  let status = "ok";
  if (!ofKind.length) status = "none";
  else if (!active.length) status = "disabled";
  else if (capped.length === active.length) status = "all_capped";
  return {
    status,
    configured: ofKind.length,
    active: active.length,
    placeholders: all.filter((u) => isPlaceholder(u.vpa)).map((u) => u.vpa),
    next: routable[0]?.vpa || null,
    live: live.map(row),
    standby: routable.slice(live.length).filter((u) => !isCapped(u)).map(row),
    capped: capped.map(row),
  };
}

// Per-mode routing plan: { autoshuffle, mode_fallback, p2m: {...}, p2p: {...} }.
// Each mode has status: "ok" | "none" | "disabled" | "all_capped", `next` (the
// VPA the next payment goes to), `live` (VPAs currently sharing payments),
// `standby` (take over once live ones hit their cap), `capped`, `placeholders`
// and `routes_to` ("p2m" | "p2p" | null — where its payments really go).
export function routingPlan(upiIds = [], merchant = null, serverPlan = null) {
  if (serverPlan && serverPlan.p2m && serverPlan.p2p) return serverPlan;
  const autoshuffle = merchant?.autoshuffle !== false;
  const modeFallback = merchant?.mode_fallback !== false;
  const plan = {
    autoshuffle,
    mode_fallback: modeFallback,
    p2m: planForKind(upiIds, "merchant", autoshuffle),
    p2p: planForKind(upiIds, "personal", autoshuffle),
  };
  for (const [mode, other] of [["p2m", "p2p"], ["p2p", "p2m"]]) {
    plan[mode].routes_to = plan[mode].next ? mode : modeFallback && plan[other].next ? other : null;
  }
  return plan;
}

// kind: "merchant" (P2M/settlement, default) | "personal" (P2P) | "any".
// Active VPAs of that kind in routing order (head = where the next payment goes).
export function settlementVpas(upiIds = [], kind = "merchant", autoshuffle = true) {
  const active = (upiIds || []).filter((u) => isActive(u) && (kind === "any" || kindOf(u) === kind));
  return ordered(active, autoshuffle).map((u) => u.vpa);
}

export function primaryVpa(upiIds = []) {
  return settlementVpas(upiIds)[0] || "";
}

// Short human label for where funds settle, e.g. "shop@okhdfc" or
// "shop@okhdfc +2 more". Returns "" when there's no active VPA.
export function settlementLabel(upiIds = []) {
  const list = settlementVpas(upiIds);
  if (!list.length) return "";
  if (list.length === 1) return list[0];
  return `${list[0]} +${list.length - 1} more`;
}
